/**
 * 🚗 HOST VEHICLE APPLICATIONS LIST
 * Component for displaying and managing host vehicle applications
 * Integrates with new backend APIs for vehicle approval workflow
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Paper,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Stack,
  LinearProgress,
  Badge,
  InputLabel,
  TablePagination
} from '@mui/material';
import {
  DirectionsCar,
  CheckCircle,
  Cancel,
  Schedule,
  Visibility,
  Refresh,
  FilterList,
  Person,
  Description,
  Camera,
  Download,
  DriveEta,
  LocalGasStation,
  Star,
  Verified,
  Warning,
  Collections
} from '@mui/icons-material';
import { hostVehicleService, type HostVehicleApplication } from '@/services/hostVehicleService';
import HostVehicleDetailViewer from './HostVehicleDetailViewer';
import { VehicleImageManagementDialog } from '@/components/vehicles/VehicleImageManagementDialog';

interface HostVehicleApplicationsListProps {
  onStatsUpdate?: (stats: any) => void;
}

interface FilterState {
  status: 'all' | 'pending_review' | 'approved' | 'rejected' | 'incomplete';
  vehicle_type: 'all' | 'economy' | 'premium' | 'luxury' | 'suv' | 'electric';
  sortBy: 'submitted_at' | 'reviewed_at' | 'make' | 'model';
  sortOrder: 'asc' | 'desc';
  search: string;
  dateRange: 'all' | 'today' | 'week' | 'month' | 'custom';
  startDate: string;
  endDate: string;
}

const HostVehicleApplicationsList: React.FC<HostVehicleApplicationsListProps> = ({
  onStatsUpdate
}) => {
  const [applications, setApplications] = useState<HostVehicleApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<HostVehicleApplication | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [detailViewerOpen, setDetailViewerOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });
  const [imageManagementDialog, setImageManagementDialog] = useState<{
    open: boolean;
    vehicleId: string;
    vehicleName: string;
  }>({ open: false, vehicleId: '', vehicleName: '' });
  
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    vehicle_type: 'all',
    sortBy: 'submitted_at',
    sortOrder: 'desc',
    search: '',
    dateRange: 'all',
    startDate: '',
    endDate: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  const rejectionReasons = [
    'Incomplete documentation',
    'Invalid vehicle registration',
    'Expired insurance certificate',
    'Vehicle does not meet age requirements',
    'Poor vehicle condition in photos',
    'Invalid driver license',
    'Vehicle already registered by another host',
    'Insurance coverage insufficient',
    'Other (specify in notes)'
  ];

  // Helper: Calculate date range
  const getDateRange = (): { startDate?: string; endDate?: string } => {
    const now = new Date();
    const endDate = now.toISOString();

    switch (filters.dateRange) {
      case 'today': {
        const startOfDay = new Date(now);
        startOfDay.setHours(0, 0, 0, 0);
        return { startDate: startOfDay.toISOString(), endDate };
      }
      case 'week': {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { startDate: weekAgo.toISOString(), endDate };
      }
      case 'month': {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return { startDate: monthAgo.toISOString(), endDate };
      }
      case 'custom':
        if (filters.startDate && filters.endDate) {
          return {
            startDate: new Date(filters.startDate).toISOString(),
            endDate: new Date(filters.endDate).toISOString()
          };
        }
        return {};
      default:
        return {};
    }
  };

  // Load host vehicle applications
  const loadHostVehicleApplications = async () => {
    setIsLoading(true);
    try {
      const dateRange = getDateRange();

      const params = {
        status: filters.status !== 'all' ? filters.status : undefined,
        vehicle_type: filters.vehicle_type !== 'all' ? filters.vehicle_type : undefined,
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };

      const result = await hostVehicleService.getHostVehicleApplications(params);
      
      if (result.success && result.data) {
        setApplications(result.data.data);
        setPagination(prev => ({
          ...prev,
          total: result.data!.total
        }));
        
        // Update stats for parent component
        if (result.data.stats) {
          onStatsUpdate?.(result.data.stats);
        }
      } else {
        console.error('Failed to load host vehicle applications:', result.error);
        setSnackbar({
          open: true,
          message: 'Failed to load applications',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading host vehicle applications:', error);
      setSnackbar({
        open: true,
        message: 'Error loading applications',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle approve/reject actions
  const handleAction = (application: HostVehicleApplication, action: 'approve' | 'reject') => {
    setSelectedApplication(application);
    setActionType(action);
    setActionDialogOpen(true);
    setActionNotes('');
    setRejectionReason('');
  };

  const handleActionConfirm = async () => {
    if (!selectedApplication || !actionType) return;

    setIsLoading(true);
    try {
      let result;
      
      if (actionType === 'approve') {
        result = await hostVehicleService.approveHostVehicle(
          selectedApplication.vehicle_id,
          actionNotes || 'Vehicle approved for hosting'
        );
      } else {
        const finalReason = rejectionReason || 'Application rejected';
        result = await hostVehicleService.rejectHostVehicle(
          selectedApplication.vehicle_id,
          finalReason,
          actionNotes || finalReason
        );
      }

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Vehicle ${actionType}d successfully`,
          type: 'success'
        });
        
        // Refresh the list
        await loadHostVehicleApplications();
      } else {
        setSnackbar({
          open: true,
          message: `Failed to ${actionType} vehicle`,
          type: 'error'
        });
      }
    } catch (error) {
      console.error(`Error ${actionType}ing vehicle:`, error);
      setSnackbar({
        open: true,
        message: `Error ${actionType}ing vehicle`,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setActionDialogOpen(false);
      setSelectedApplication(null);
      setActionType(null);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending_review: 'warning',
      approved: 'success',
      rejected: 'error',
      incomplete: 'default'
    } as const;
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      pending_review: <Schedule />,
      approved: <CheckCircle />,
      rejected: <Cancel />,
      incomplete: <Warning />
    };
    return icons[status as keyof typeof icons] || <Warning />;
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      economy: <DirectionsCar />,
      premium: <Star />,
      luxury: <Verified />,
      suv: <DriveEta />,
      electric: <LocalGasStation />
    };
    return icons[category as keyof typeof icons] || <DirectionsCar />;
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      economy: 'default',
      premium: 'primary',
      luxury: 'secondary',
      suv: 'success',
      electric: 'info'
    } as const;
    return colors[category as keyof typeof colors] || 'default';
  };

  const filteredApplications = applications.filter(app => {
    if (filters.vehicle_type !== 'all' && app.vehicle_type !== filters.vehicle_type) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    loadHostVehicleApplications();
  }, [filters, pagination.page, pagination.limit]);

  return (
    <Box>
      {/* Header with controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" component="div">
          Host Vehicle Applications
          <Chip
            label={`${pagination.total} total`}
            size="small"
            sx={{ ml: 1 }}
          />
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<Refresh />}
            onClick={loadHostVehicleApplications}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Filters Row */}
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search vehicles, hosts, plates..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={filters.dateRange}
                label="Date Range"
                onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value as any }))}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {filters.dateRange === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6} md={filters.dateRange === 'custom' ? 1.5 : 2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending_review">Pending Review</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="incomplete">Incomplete</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={filters.dateRange === 'custom' ? 1.5 : 2}>
            <FormControl fullWidth size="small">
              <InputLabel>Vehicle Type</InputLabel>
              <Select
                value={filters.vehicle_type}
                label="Vehicle Type"
                onChange={(e) => setFilters(prev => ({ ...prev, vehicle_type: e.target.value as any }))}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="economy">Economy</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="luxury">Luxury</MenuItem>
                <MenuItem value="suv">SUV</MenuItem>
                <MenuItem value="electric">Electric</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={filters.dateRange === 'custom' ? 1.5 : 2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value as any }))}
              >
                <MenuItem value="submittedAt">Submitted Date</MenuItem>
                <MenuItem value="reviewedAt">Reviewed Date</MenuItem>
                <MenuItem value="make">Make</MenuItem>
                <MenuItem value="model">Model</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Loading indicator */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Results table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Host & Vehicle</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Insurance</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApplications.map((application) => (
                <TableRow key={application.vehicle_id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, width: 40, height: 40 }}>
                        {application.host_name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {application.host_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {application.year} {application.make} {application.model}
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {application.plate_number}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Chip
                        icon={getCategoryIcon(application.vehicle_type)}
                        label={application.vehicle_type.toUpperCase()}
                        color={getCategoryColor(application.vehicle_type)}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(application.status)}
                      label={application.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(application.status)}
                      size="small"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {application.insurance_provider || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {application.monthly_premium ? `₦${application.monthly_premium.toLocaleString()}/month` : 'Not specified'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(application.submitted_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(application.submitted_at).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Badge badgeContent={
                      (application.vehicle_registration_url ? 1 : 0) +
                      (application.insurance_certificate_url ? 1 : 0) +
                      (application.driver_license_url ? 1 : 0) +
                      (application.vehicle_photos?.length || 0)
                    } color="primary">
                      <Description fontSize="small" />
                    </Badge>
                  </TableCell>
                  
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Manage Images">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setImageManagementDialog({
                            open: true,
                            vehicleId: application.vehicle_id,
                            vehicleName: `${application.year} ${application.make} ${application.model}`
                          })}
                        >
                          <Collections />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedApplication(application);
                            setDetailViewerOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {application.status === 'pending_review' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleAction(application, 'approve')}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Reject">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleAction(application, 'reject')}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1} // Material-UI uses 0-based indexing
          onPageChange={(event, newPage) => {
            setPagination(prev => ({ ...prev, page: newPage + 1 }));
          }}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(event) => {
            setPagination(prev => ({ 
              ...prev, 
              limit: parseInt(event.target.value, 10),
              page: 1 // Reset to first page when changing rows per page
            }));
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Applications per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Card>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm {actionType === 'approve' ? 'Approval' : 'Rejection'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to {actionType} this vehicle application for{' '}
            <strong>
              {selectedApplication?.year} {selectedApplication?.make} {selectedApplication?.model}
            </strong>{' '}
            by <strong>{selectedApplication?.host_name}</strong>?
          </Typography>
          
          {actionType === 'reject' && (
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>Rejection Reason</InputLabel>
              <Select
                value={rejectionReason}
                label="Rejection Reason"
                onChange={(e) => setRejectionReason(e.target.value)}
              >
                {rejectionReasons.map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label={`Admin Notes (${actionType === 'approve' ? 'Optional' : 'Required if Other'})`}
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            margin="normal"
            placeholder={`Add notes about this ${actionType} decision...`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            disabled={isLoading || (actionType === 'reject' && !rejectionReason)}
          >
            {isLoading ? 'Processing...' : `${actionType === 'approve' ? 'Approve' : 'Reject'} Vehicle`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Viewer Modal */}
      {selectedApplication && (
        <HostVehicleDetailViewer
          open={detailViewerOpen}
          onClose={() => {
            setDetailViewerOpen(false);
            setSelectedApplication(null);
          }}
          application={selectedApplication}
          onApprove={async (vehicleId, notes) => {
            try {
              await hostVehicleService.approveHostVehicle(vehicleId, notes || 'Vehicle approved for hosting');
              setSnackbar({ open: true, message: 'Vehicle approved successfully', type: 'success' });
            } catch (error) {
              console.error('Error approving vehicle:', error);
              setSnackbar({ open: true, message: 'Failed to approve vehicle', type: 'error' });
            } finally {
              setDetailViewerOpen(false);
              await loadHostVehicleApplications();
            }
          }}
          onReject={async (vehicleId, reason, notes) => {
            try {
              await hostVehicleService.rejectHostVehicle(vehicleId, reason, notes || reason);
              setSnackbar({ open: true, message: 'Vehicle rejected', type: 'success' });
            } catch (error) {
              console.error('Error rejecting vehicle:', error);
              setSnackbar({ open: true, message: 'Failed to reject vehicle', type: 'error' });
            } finally {
              setDetailViewerOpen(false);
              await loadHostVehicleApplications();
            }
          }}
        />
      )}

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Vehicle Image Management Dialog */}
      {imageManagementDialog.open && (
        <VehicleImageManagementDialog
          open={imageManagementDialog.open}
          onClose={() => setImageManagementDialog({ open: false, vehicleId: '', vehicleName: '' })}
          vehicleId={imageManagementDialog.vehicleId}
          vehicleName={imageManagementDialog.vehicleName}
        />
      )}
    </Box>
  );
};

export default HostVehicleApplicationsList;